/* eslint-disable ember/no-runloop */
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismCard } from '@mylighthouse/prism-ember';
import { PRISM_SPACING_500 } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { not } from 'ember-truth-helpers';
import type * as Highcharts from 'highcharts';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import ParityTooltipsTooltipKpi from 'lumen/components/parity/tooltips/tooltip-kpi';
import type { ParityStrategyEvolution } from 'lumen/models';
import { xValue } from 'lumen/utils/charts/chart-utils';
import {
  PARITY_COLORS,
  PREDICTED_PARITY_COLORS,
} from 'lumen/utils/constants/chart-color-constants';
import {
  HIGHCHARTS_MISSING_MARKER,
  HIGHCHARTS_SELECTED_MARKER,
} from 'lumen/utils/constants/general-constants';
import type {
  ChartCustomPoint,
  CustomGraphic,
  CustomSeries,
} from 'lumen/utils/constants/highcharts-constants';
import {
  PARITY_FILTER,
  type ParityFilter,
} from 'lumen/utils/constants/pi-constant-values';

const BASE_Z_INDEX = 10;

interface LossEntry {
  isLatest: boolean;
  isMissing: boolean;
  isSelectable: boolean;
  marker?: {
    enabled: boolean;
  };
  totalNumberOfRecords: number;
  type: ParityFilter;
  x: number;
  y: number;
}

interface ParityStrategyCustomPoint extends ChartCustomPoint {
  isLatest?: boolean;
  isSelectable?: boolean;
}

type PointLossEntry = LossEntry & ParityStrategyCustomPoint;

type ChartSeries = Omit<Highcharts.SeriesAreaOptions, 'data'> & {
  data: LossEntry[];
};

interface TooltipValues {
  benchmark?: number;
  hasNoData: boolean;
  loss: { value: number };
  totalNbOfHotels: number;
  totalNbOfShops: number;
}

interface ParityChartsChartParityStrategyEvolutionSignature {
  Args: {
    customLegendItemClick: (filter: ParityFilter) => void;
    data: ParityStrategyEvolution[];
    extractDate: string;
    isBenchmarkSelected: boolean;
    isLatestSelected: boolean;
    isLoading: boolean;
    updateExtractDate: (date: string) => void;
  };
}

export default class ParityChartsChartParityStrategyEvolution extends Component<ParityChartsChartParityStrategyEvolutionSignature> {
  @service declare intl: Services['intl'];

  @service declare psSelections: Services['ps-selections'];

  @tracked tooltipTitle = '';
  @tracked tooltipValues: TooltipValues | null = null;

  get chartData(): ChartSeries[] {
    if (this.args.isLoading) {
      return [];
    }

    const lossData: LossEntry[] = [];
    const lossZones: Highcharts.SeriesZonesOptionsObject[] = [];

    this.args.data.forEach((item) => {
      const x = xValue(item.extractDate);
      const isLatestSelected = this.args.isLatestSelected && item.isLatest;

      const lossEntry: LossEntry = {
        x,
        y: item.lossScore,
        type: PARITY_FILTER.LOSS,
        totalNumberOfRecords: item.numberOfRecords,
        isLatest: item.isLatest,
        isMissing: item.isMissing,
        isSelectable: !item.isMissing,
      };

      const lossZoneEntry: Highcharts.SeriesZonesOptionsObject = {
        value: x,
        dashStyle: 'Solid',
      };
      if (item.isLatest) {
        lossZoneEntry.dashStyle = 'Dash';
        lossZoneEntry.fillColor =
          PREDICTED_PARITY_COLORS[PARITY_FILTER.LOSS].fillColor;
        lossZoneEntry.color = PREDICTED_PARITY_COLORS[PARITY_FILTER.LOSS].color;
      }

      if (item.isMissing) {
        lossEntry.marker = HIGHCHARTS_MISSING_MARKER;
      } else if (
        item.extractDate === this.args.extractDate ||
        isLatestSelected
      ) {
        lossEntry.marker = HIGHCHARTS_SELECTED_MARKER;
      } else {
        lossEntry.marker = undefined;
      }

      lossData.push(lossEntry);
      lossZones.push(lossZoneEntry);
    });

    const chartData: ChartSeries[] = [];
    chartData.push({
      id: PARITY_FILTER.LOSS,
      name: this.intl.t('parity_analytics.loss').toString(),
      data: lossData,
      type: 'area',
      zoneAxis: 'x',
      zones: lossZones,
      zIndex: BASE_Z_INDEX,
      fillColor: PARITY_COLORS[PARITY_FILTER.LOSS].fillColor,
      color: PARITY_COLORS[PARITY_FILTER.LOSS].color,
    });

    return chartData;
  }

  get chartOptions(): Highcharts.Options {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return {
      chart: {
        type: 'area',
        height: '260px',
        spacingTop: PRISM_SPACING_500,
        spacingBottom: 0,
        spacingLeft: PRISM_SPACING_500,
        spacingRight: PRISM_SPACING_500,
        zoomType: undefined,
        animation: false,
      },
      title: {
        text: 'parity-evolution-chart',
      },
      tooltip: {
        positioner: undefined,
        shared: true,
        hideDelay: 0.2,
        shape: 'square',
        padding: 0,
        borderWidth: 0,
        shadow: false,
        outside: true,
        distance: 40,

        formatter(): string | undefined {
          begin();

          const numberOfPoints = this.points?.length;

          if (numberOfPoints && this.points && this.points[0]) {
            const point = this.points[0].point as PointLossEntry;
            const tooltipValues: TooltipValues = {
              loss: { value: point.y },
              hasNoData: point.isMissing,
              totalNbOfHotels: point.totalNumberOfRecords,
              totalNbOfShops: point.totalNumberOfRecords,
            };

            that.tooltipValues = tooltipValues;

            if (point.isLatest) {
              that.tooltipTitle = that.intl
                .t('benchmark.current_score')
                .toString();
            } else {
              const formattedExtractDate = dayjs
                .unix(this.x)
                .utc()
                .format('ll');
              const formattedExtractDateEndOfWeek = dayjs
                .unix(this.x)
                .add(6, 'day')
                .format('ll');
              that.tooltipTitle = `${formattedExtractDate} - ${formattedExtractDateEndOfWeek}`;
            }
          }
          end();
          return document.getElementById('dummy-tooltip-for-history')
            ?.innerHTML;
        },
        useHTML: true,
      },

      xAxis: {
        allowDecimals: false,
        type: 'datetime',
        endOnTick: true,
        ordinal: false,
        tickPositioner() {
          if (this.series[0]) {
            return (this.series[0] as CustomSeries).processedXData.slice();
          }
          return null;
        },
        labels: {
          rotation: -45,
          align: 'right',
          x: 10,
          formatter() {
            const { data } = that.args;
            const extractDate = data.at(-1)?.extractDate;

            const value = this.value as number;

            if (extractDate && xValue(extractDate) === value) {
              return that.intl.t('general.today').toString();
            }

            // Show the last day of the week
            return dayjs.unix(value).add(6, 'day').utc().format('ll');
          },
        },
      } as Highcharts.XAxisOptions,
      yAxis: [
        {
          allowDecimals: true,
          endOnTick: true,
          showFirstLabel: false,
          min: 0,
          max: this.maxYValue,
          title: { text: '' },
          labels: {
            formatter(): string {
              const value = this.value as number;

              return that.intl.formatNumber(value, { style: 'percent' });
            },
          },
        },
      ] as Highcharts.YAxisOptions[],
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        enabled: true,
        borderWidth: 0,
        symbolWidth: 7,
        symbolHeight: 7,
        itemStyle: {
          width: 200,
          fontSize: '12px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          color: 'var(--prism-color-text-neutral-subdued)',
        },
        itemHoverStyle: {
          color: 'var(--prism-color-interactive-text-secondary-hover)',
        },
      },
      plotOptions: {
        area: {
          stacking: 'normal',
          fillOpacity: 1,
          opacity: 1,
          point: {
            events: {
              click(e): void {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const customPoint: ParityStrategyCustomPoint = this;
                // Timestamp * 1000 for highcharts x-axis conversion to real unix timestamp
                if (customPoint.isSelectable) {
                  const formattedPeriod = dayjs
                    .utc(e.point.x * 1000)
                    .format('YYYY-MM-DD');
                  that.args.updateExtractDate(
                    (e.point as ParityStrategyCustomPoint).isLatest
                      ? ''
                      : formattedPeriod,
                  );
                }
              },
              mouseOver(): void {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const customPoint: ParityStrategyCustomPoint = this;
                const cursor = customPoint.isSelectable ? 'pointer' : 'auto';
                // We use parentGroup because otherwise the cursor only changes when hovering on the inside
                if (
                  (customPoint.graphic as CustomGraphic).parentGroup.element
                    .style
                ) {
                  (
                    customPoint.graphic as CustomGraphic
                  ).parentGroup.element.style.cursor = cursor;
                }
              },
            },
          },
        },
        line: { enableMouseTracking: false }, // Prevents benchmark line from stopping click events on marker
        series: {
          events: {
            legendItemClick(event): void {
              if (that.args.customLegendItemClick) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                event.preventDefault();
                that.args.customLegendItemClick(
                  this.options.id as ParityFilter,
                );
              }
            },
          },
        },
      },
    };
  }

  get maxYValue(): number {
    if (this.args.isLoading) {
      return 1;
    }
    return this.args.data.reduce(
      (max, item) => Math.max(max, item.lossScore),
      0,
    );
  }

  <template>
    <PrismCard @contentLayout="noPadding">
      <:content>
        <GeneralChartsOtaChart
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
        />
        {{#if this.tooltipValues}}
          <div class="parity-tooltip hidden" id="dummy-tooltip-for-history">
            <ParityTooltipsTooltipKpi
              @data={{this.tooltipValues}}
              @decimalPrecision="0%"
              @displayName={{this.tooltipTitle}}
              @hideNbOfShops={{not this.psSelections.isHotelSelected}}
              @hideShoppedHotels={{this.psSelections.isHotelSelected}}
            />
          </div>
        {{/if}}
      </:content>
    </PrismCard>
  </template>
}
